import { useQuery } from '@tanstack/react-query';
import { formatIstDate, formatMoney, inboxApi } from '@wa/shared';

/**
 * One GST tax invoice per top-up.
 *
 * The tax is shown split as it appears on the document — CGST and SGST for a client
 * in Maharashtra, IGST for one anywhere else — because that split is the thing their
 * accountant checks, and a single "tax" total hides the one field that can be wrong.
 */
export function InvoicesPanel() {
  const invoices = useQuery({ queryKey: ['invoices'], queryFn: inboxApi.invoices });
  const rows = invoices.data ?? [];

  return (
    <section className="rounded-lg border border-(--color-line) bg-(--color-surface)">
      <h2 className="border-b border-(--color-line) px-3 py-2.5 text-body font-semibold sm:px-4">
        {rows.length} {rows.length === 1 ? 'invoice' : 'invoices'}
      </h2>

      {rows.length === 0 ? (
        <p className="px-3 py-6 text-center text-small text-(--color-muted) sm:px-4">
          An invoice is raised each time you add money. Nothing has been added yet.
        </p>
      ) : (
        <ul className="divide-y divide-(--color-line-dim)">
          {rows.map((invoice) => (
            <li key={invoice.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2.5 sm:px-4">
              <div className="min-w-0 flex-1 basis-44">
                <p className="truncate font-mono text-small">{invoice.number}</p>
                <p className="truncate text-micro text-(--color-muted)">
                  {formatIstDate(invoice.issuedAt)} · ₹{formatMoney(invoice.taxableAmount)} +{' '}
                  {invoice.igst > 0
                    ? <>IGST ₹{formatMoney(invoice.igst)}</>
                    : <>CGST ₹{formatMoney(invoice.cgst)} + SGST ₹{formatMoney(invoice.sgst)}</>}
                </p>
              </div>

              {invoice.status === 1 && (
                <span className="shrink-0 rounded-(--radius-chip) bg-(--color-sunken) px-1.5 py-0.5 text-micro text-(--color-muted)">
                  cancelled
                </span>
              )}

              <span className="shrink-0 tabular-nums text-small font-medium">
                ₹{formatMoney(invoice.total)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
